/**
 * CashTransactionsTab — Cash-account detail sub-tab.
 *
 * Reuses the same filter primitives as the top-level /transactions
 * page (search, type filter, category filter, date-range chips) but
 * scoped to a single account_id. Data is queried once on mount and
 * filtered client-side for snappy interactions.
 *
 * No writes. This is a pure read view.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Calendar as CalendarIcon } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { formatMoney } from '@/lib/format';
import {
  EXPENSE_CATEGORIES, INCOME_CATEGORIES, INVESTMENT_CATEGORIES,
  TRANSFER_CATEGORIES, REFUND_CATEGORIES,
} from '@/lib/categorize';
import { RANGE_PRESETS, resolveRange, txnInRange } from '@/lib/dateRange';
import { ACCOUNTS_TONE } from '@/lib/accountsDemo';

const ALL_CATEGORIES = Array.from(new Set([
  ...EXPENSE_CATEGORIES, ...INCOME_CATEGORIES, ...INVESTMENT_CATEGORIES,
  ...TRANSFER_CATEGORIES, ...REFUND_CATEGORIES,
])).sort();

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

export default function CashTransactionsTab({ account, baseCurrency = 'INR' }) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search,       setSearch]      = useState('');
  const [filterType,   setFilterType]  = useState('all');
  const [filterCat,    setFilterCat]   = useState('all');
  const [rangePreset,  setRangePreset] = useState('last_30');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // Phase 82.9 — authoritative column is `txn_date`, not `date`.
        // Prior query silently returned zero rows (see CashDetail comment).
        const { data } = await supabase
          .from('transactions')
          .select('id, txn_date, description, amount, category, type')
          .eq('account_id', account.id)
          .order('txn_date', { ascending: false })
          .limit(500);
        if (!cancelled) setTxns(data || []);
      } catch (e) {
        console.warn('[CashTransactionsTab] load failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [account?.id]);

  const dateRange = useMemo(() => resolveRange(rangePreset), [rangePreset]);

  const filtered = useMemo(() => {
    return txns.filter((t) => {
      if (filterType !== 'all' && t.type !== filterType) return false;
      if (filterCat  !== 'all' && (t.category || 'Miscellaneous') !== filterCat) return false;
      // Phase 82.9 — txnInRange signature is (dateString, from, to). Prior
      // call passed the whole txn object and dateRange, which coerced the
      // txn to a string and compared it against undefined bounds — always
      // returned true, so the range filter did nothing.
      if (!txnInRange(t.txn_date, dateRange.from, dateRange.to)) return false;
      if (search) {
        const hay = `${t.description || ''} ${t.category || ''}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [txns, filterType, filterCat, search, dateRange]);

  const totalIn  = filtered.reduce((s, t) => s + (Number(t.amount) > 0 || t.type === 'income' ? Math.abs(Number(t.amount) || 0) : 0), 0);
  const totalOut = filtered.reduce((s, t) => s + ((t.type !== 'income' && Number(t.amount) <= 0) ? Math.abs(Number(t.amount) || 0) : 0), 0);

  return (
    <div className="space-y-4" data-testid="accounts-cash-tab-transactions">
      {/* Filters row */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-[1fr_160px_180px] gap-2">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search description or category…"
            className="rounded-xl pl-9"
            data-testid="cash-txn-search"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="rounded-xl" data-testid="cash-txn-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="investment">Investment</SelectItem>
            <SelectItem value="refund">Refund</SelectItem>
            <SelectItem value="transfer">Transfer</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="rounded-xl" data-testid="cash-txn-category"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All categories</SelectItem>
            {ALL_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Date range chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="cash-txn-range-chips">
        <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
        {RANGE_PRESETS.filter((p) => p.value !== 'custom').map((p) => {
          const isActive = rangePreset === p.value;
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => setRangePreset(p.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${isActive
                ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                : 'bg-muted/40 text-muted-foreground hover:bg-muted/70'}`}
              data-testid={`cash-txn-range-${p.value}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Aggregate strip */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground px-1">
        <span data-testid="cash-txn-count">{filtered.length} transaction{filtered.length !== 1 ? 's' : ''}</span>
        <span className="space-x-3">
          <span>In: <span className="num text-charcoal font-medium" style={{ color: ACCOUNTS_TONE.positive }}>+{formatMoney(totalIn, baseCurrency)}</span></span>
          <span>Out: <span className="num text-charcoal font-medium">−{formatMoney(totalOut, baseCurrency)}</span></span>
        </span>
      </div>

      {/* List */}
      <section className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-5 space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-12 text-center text-xs text-muted-foreground" data-testid="cash-txn-empty">
            No transactions match those filters.
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {filtered.map((t) => {
              const amt = Number(t.amount || 0);
              const positive = t.type === 'income' || amt > 0;
              return (
                <li key={t.id} className="flex items-center gap-4 px-5 py-3.5">
                  <div className="w-8 h-8 rounded-lg bg-offwhite flex items-center justify-center text-[11px] font-semibold text-muted-foreground shrink-0">
                    {(t.description || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-charcoal truncate">{t.description || 'Transaction'}</p>
                    <p className="text-[11px] text-muted-foreground">{t.category || '—'} · {fmtDate(t.txn_date)}</p>
                  </div>
                  <p
                    className="num text-sm font-semibold shrink-0"
                    style={{ color: positive ? ACCOUNTS_TONE.positive : '#1E1E1E' }}
                  >
                    {positive ? '+' : '−'}{formatMoney(Math.abs(amt), baseCurrency)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
